import React from 'react';
import bowser from 'bowser';
import {
    EAppEnvironment,
    EQueryStringParam,
    loginLinkTtlMinutes,
    EUnsupportedReason,
    EWebClientsBaseUrl,
    EIdentityClientId,
    EMixpanelAppName,
} from './constants';
import { TBusiness, TUser, TBrowserSupport } from './types';

export const checkBrowserIsSupported = (): TBrowserSupport => {
    const userBrowser = bowser.getParser(window.navigator.userAgent);
    if (userBrowser.isBrowser('Internet Explorer')) {
        return {
            isSupported: false,
            unSupportedReason: EUnsupportedReason.unsupportedBrowser,
        };
    }
    if (userBrowser.isBrowser('Safari')) {
        const fullVersion = userBrowser.getBrowserVersion();
        if (fullVersion && getMajorVersion(fullVersion) < 12) {
            return {
                isSupported: false,
                unSupportedReason: EUnsupportedReason.unsupportedBrowser,
            };
        }
    }
    if (!window.navigator.cookieEnabled) {
        return {
            isSupported: false,
            unSupportedReason: EUnsupportedReason.cookiesDisabled,
        };
    }
    return {
        isSupported: true,
        unSupportedReason: null,
    };
};

const getMajorVersion = (version: string): number => {
    return parseInt(version.split('.')[0]);
};

export const removeUrlProtocol = (url: string): string => url.replace(/(^\w+:|^)\/\//, '');

export const getInitials = (firstName: string, lastName: string): string =>
    `${firstName.charAt(0).toUpperCase()}${lastName.charAt(0).toUpperCase()}`;

export const parseEnvironment = (environment: string): string => {
    if (environment.includes(EAppEnvironment.ngrok)) {
        return EAppEnvironment.ngrok;
    }
    return environment;
};

export const parseSubscriberId = (): string | null => {
    const decodedReturnUrlParam = decodeURIComponent(
        new URLSearchParams(window.location.search).get(EQueryStringParam.returnUrl),
    );
    const allDecodedParams = new URLSearchParams(decodedReturnUrlParam.split('?')[1]);
    const subscriberId = allDecodedParams.get(EQueryStringParam.subscriberId);
    return subscriberId;
};

export const matchName = (name: string, nameToMatch: string): boolean => {
    const name1 = name.toLocaleLowerCase();
    const name2 = nameToMatch.toLocaleLowerCase();
    return name1 === name2;
};

export const replacePhrase = (phrase: string, phraseToBeReplaced: string | RegExp, replaceValue: string) => {
    return phrase.replace(phraseToBeReplaced, replaceValue);
};

// JSX object must have key
export const jsxInString = (str: string, match: RegExp | string, element: React.ReactNode) => {
    let result;
    const foundMatch = str.match(match);
    if (foundMatch) {
        // replacing with a placeholder string because you can't `split` a string with a Regular Expression
        const placeholder = '<PLACEHOLDER>';
        const splitAtPlaceholder: React.ReactNode[] = str.replace(match, placeholder).split(placeholder);

        // first value is the index at which `element` is inserted
        // second value is how many characters to delete
        // third value is adding the `element`
        splitAtPlaceholder.splice(1, 0, element);

        const cleanUpEmptyStrings = splitAtPlaceholder.filter((item) => item !== '');

        result = cleanUpEmptyStrings.slice();
    } else {
        result = [str];
    }

    return result as React.ReactNode;
};

export const replaceElement = (
    phrase: string,
    matchWithoutCarets: string,
    ComponentBeingAdded: React.ReactElement<any>,
) => {
    // creating a temporary placeholder because we are going to replace
    // the wrapping element and text to keep with it before replacing it
    const placeholder = '<PLACEHOLDER>';
    const clonePhrase = `${phrase}`;
    const openingTag = `<${matchWithoutCarets}>`;
    const closingTag = `</${matchWithoutCarets}>`;

    // find the opening/closing tags
    const textStartIndex = phrase.indexOf(openingTag) + openingTag.length;
    const closingTagIndex = phrase.indexOf(closingTag);

    // just get the phrase that is being wrapped
    const text = clonePhrase.slice(textStartIndex, closingTagIndex);

    // remove the page phrase tags and text and replace with just the placeholder
    const phraseWithPlaceholder = phrase.replace(openingTag + text + closingTag, placeholder);

    // replace the placeholder with the component being added and make the text as children
    const result = jsxInString(
        phraseWithPlaceholder,
        placeholder,
        React.cloneElement(ComponentBeingAdded, {
            children: text,
            key: ComponentBeingAdded.key || 'formattedComponent',
        }),
    );

    return result;
};

export type TParseUserBusinessesData = {
    primaryUserId: string;
    businesses: TBusiness[];
};
export const parseUserBusinesses = (data: TParseUserBusinessesData) => {
    // setup variables
    const primaryUserId = data.primaryUserId;
    const businesses = data.businesses;
    let tempPrimaryUser: TUser = { id: '', firstName: '', lastName: '' };
    let tempHasFamilyUsers = false;
    let businessesWithUsersSorted: TBusiness[] = [];

    // iterate over businesses
    businesses.forEach((singleBusiness) => {
        // clone business for safe mutation
        let singleBusinessWithSortedUsers: TBusiness = { ...singleBusiness };
        // iterate over business users
        singleBusiness.users.forEach((user, index, usersArray) => {
            // if user is primary user
            if (user.id === primaryUserId) {
                // clone users array for safe mutation
                const sortedUsers = [...usersArray];
                // put primary user in first user position
                sortedUsers.splice(index, 1);
                sortedUsers.unshift(user);
                // put single business object back together with sorted users
                singleBusinessWithSortedUsers = { ...singleBusinessWithSortedUsers, users: sortedUsers };
                // if primary user has not been set, set primary user
                if (!tempPrimaryUser.id && user.id === primaryUserId) {
                    tempPrimaryUser = user;
                }
            } else if (!tempHasFamilyUsers) {
                // if non primary users exist its a family user
                tempHasFamilyUsers = true;
            }
        });
        // put sorted single business into sorted businesses array
        businessesWithUsersSorted = [...businessesWithUsersSorted, singleBusinessWithSortedUsers];
    });
    return {
        businesses: businessesWithUsersSorted,
        primaryUser: tempPrimaryUser,
        hasFamilyUsers: tempHasFamilyUsers,
    };
};

export const loginIsWithinTimeWindow = (epochTime: number): boolean => {
    const now = Date.now();
    const elapsedMinutes = (now - epochTime) / 60000;
    if (elapsedMinutes > loginLinkTtlMinutes) {
        return false;
    }
    return true;
};

export const getClientsStudioUrl = (webClientsBaseUrl: EWebClientsBaseUrl, subscriberId: string): string =>
    `${webClientsBaseUrl}/classic/home?studioid=${subscriberId}`;

export const mapIdentityClientIdToMixpanelSurface = (clientId: EIdentityClientId): EMixpanelAppName | null => {
    switch (clientId) {
        case EIdentityClientId.brandedWeb:
            return EMixpanelAppName.brandedWeb;
        case EIdentityClientId.messengerAi:
            return EMixpanelAppName.messengerAi;
        case EIdentityClientId.mindbodyAppIos:
        case EIdentityClientId.mindbodyAppAndroid:
            return EMixpanelAppName.mindbodyApp;
        case EIdentityClientId.mindbodyMarketplace:
            return EMixpanelAppName.mindbodyMarketplace;
        default:
            return null;
    }
};
